"use client";

/**
 * AdminTextarea — 라벨 + 여러 줄 입력 컨트롤
 *
 * AdminInput 의 textarea 버전. 메모/설명처럼 여러 줄이 필요한 필드에 사용한다.
 * 한 줄 입력이면 AdminInput 을 사용한다.
 *
 * layout 옵션:
 * - "stacked"(기본): 라벨이 textarea 위에 작게(xs, muted) 표시
 * - "inline":        라벨이 textarea 왼쪽에 한 줄로 표시
 *
 * @example
 * <AdminTextarea layout="stacked" label="메모" value={memo} onChange={setMemo} rows={4} />
 */
import { type ComponentProps, type ReactNode, useId } from "react";
import { cn } from "../../lib/utils";
import { Label } from "../ui/label";

export interface AdminTextareaProps
  extends Omit<ComponentProps<"textarea">, "onChange"> {
  /**
   * 레이아웃 방식
   * - "stacked"(기본): 라벨 위, textarea 아래
   * - "inline": 라벨 왼쪽, textarea 오른쪽
   */
  layout?: "stacked" | "inline";
  /** 필드 라벨 */
  label?: ReactNode;
  /** inline 일 때 라벨 너비 클래스 (기본 w-24) */
  labelClassName?: string;
  /** 입력값 변경 콜백 */
  onChange?: (value: string) => void;
  /** 입력 아래 도움말 / 에러 메시지 */
  hint?: ReactNode;
  /** hint 를 에러(빨간색)로 표시 */
  error?: boolean;
  className?: string;
  /**
   * useAdminForm().field() 의 반환값을 그대로 스프레드해서 쓸 수 있도록 받아두는 값.
   * AdminTextarea 는 검증 UI를 그리지 않으므로 실제로는 사용하지 않는다 (필요하면 AdminFormField 사용).
   */
  validate?: (value: string) => string | undefined;
  forceValidate?: boolean;
  /** useAdminForm().field() 반환값 스프레드용 — AdminTextarea 는 자동 포커싱을 지원하지 않으므로 사용하지 않는다. */
  autoFocusOnError?: boolean;
  /** useAdminForm().field() 반환값 스프레드용 — 위와 동일한 이유로 사용하지 않는다. */
  focusToken?: number;
}

export function AdminTextarea({
  layout = "stacked",
  label,
  labelClassName,
  onChange,
  hint,
  error,
  className,
  id: idProp,
  rows = 3,
  value,
  validate: _validate,
  forceValidate: _forceValidate,
  autoFocusOnError: _autoFocusOnError,
  focusToken: _focusToken,
  ...textareaProps
}: AdminTextareaProps) {
  const autoId = useId();
  const id = idProp ?? autoId;

  const textareaEl = (
    <textarea
      id={id}
      rows={rows}
      value={value}
      aria-invalid={error ? true : undefined}
      onChange={(e) => onChange?.(e.target.value)}
      className={cn(
        "flex w-full min-w-0 appearance-none resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors outline-none",
        "placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground",
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20"
      )}
      {...textareaProps}
    />
  );

  const hintEl = hint ? (
    <p className={cn("mt-1 text-xs", error ? "text-destructive" : "text-muted-foreground")}>
      {hint}
    </p>
  ) : null;

  if (!label) {
    return (
      <div className={className}>
        {textareaEl}
        {hintEl}
      </div>
    );
  }

  if (layout === "inline") {
    return (
      <div className={cn("flex items-start gap-3", className)}>
        <Label
          htmlFor={id}
          className={cn("shrink-0 pt-2 text-sm text-muted-foreground", labelClassName ?? "w-24")}
        >
          {label}
        </Label>
        <div className="min-w-0 flex-1">
          {textareaEl}
          {hintEl}
        </div>
      </div>
    );
  }

  // stacked
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {textareaEl}
      {hintEl}
    </div>
  );
}
